import { getPocketBase, escFilter as esc } from '../pocketbase.js';
import { MANTRA_ROLES } from '../../main/studioCalc.js';

export const DEFAULT_ROSA_BUDGET = 500;

export const DEFAULT_ROSA_ROLE_TARGETS = Object.fromEntries(
  MANTRA_ROLES.map((r) => [r, ({ P: 1, DS: 1, DC: 2, DD: 1, B: 0, E: 1, M: 1, C: 1, W: 1, T: 1, A: 1, PC: 1 })[r] ?? 0]),
);

export const DEFAULT_ROSA_MAX_PLAYERS = Object.values(DEFAULT_ROSA_ROLE_TARGETS)
  .reduce((sum, n) => sum + n, 0);

export const DEFAULT_AUCTION_NAME = 'Nuova asta';

/** Modalità uscite: semplice (anonimo) | assegnate (per avversario). */
export const EXIT_MODE_SIMPLE = 'simple';
export const EXIT_MODE_ASSIGNED = 'assigned';
export const DEFAULT_EXIT_MODE = EXIT_MODE_SIMPLE;

export function normalizeExitMode(raw) {
  return raw === EXIT_MODE_ASSIGNED ? EXIT_MODE_ASSIGNED : EXIT_MODE_SIMPLE;
}

function newOpponentId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `opp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Lista avversari: [{ id, name }, ...]. Id stabile; nome può essere vuoto (placeholder UI).
 * @param {unknown} raw
 * @returns {{ id: string, name: string }[]}
 */
export function normalizeOpponents(raw) {
  if (!Array.isArray(raw)) return [];
  const out = [];
  const seen = new Set();
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    let id = String(item.id || '').trim();
    if (!id) id = newOpponentId();
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({ id, name: String(item.name ?? '').trim() });
  }
  return out;
}

export function normalizeRoleTargets(raw) {
  const out = { ...DEFAULT_ROSA_ROLE_TARGETS };
  if (raw && typeof raw === 'object') {
    for (const r of MANTRA_ROLES) {
      if (raw[r] != null) out[r] = Math.max(0, Number(raw[r]) || 0);
    }
  }
  return out;
}

function normalizeMaxPlayers(raw) {
  if (raw == null || raw === '') return DEFAULT_ROSA_MAX_PLAYERS;
  return Math.max(0, Number(raw) || 0);
}

function normalizeBudget(raw) {
  if (typeof raw === 'number') return Math.max(0, raw);
  return DEFAULT_ROSA_BUDGET;
}

function normalizeNumParticipants(raw) {
  if (raw == null || raw === '') return null;
  const n = Math.floor(Number(raw));
  return n > 0 ? n : null;
}

const AUCTION_META_KEY = '__meta';

/** Meta asta salvata nel JSON role_targets (funziona anche senza campo PB dedicato). */
export function readAuctionMeta(rawRoleTargets) {
  const meta = rawRoleTargets?.[AUCTION_META_KEY];
  if (!meta || typeof meta !== 'object') return {};
  return meta;
}

export function buildRoleTargetsPayload(roleTargets, numParticipants) {
  const base = normalizeRoleTargets(roleTargets);
  if (numParticipants === undefined) return base;
  const np = normalizeNumParticipants(numParticipants);
  if (np == null) return base;
  return { ...base, [AUCTION_META_KEY]: { num_participants: np } };
}

export function mapAuctionRow(row) {
  const meta = readAuctionMeta(row.role_targets);
  const num_participants = normalizeNumParticipants(row.num_participants)
    ?? normalizeNumParticipants(meta.num_participants);
  return {
    id: row.id,
    access_key: row.access_key,
    name: row.name || DEFAULT_AUCTION_NAME,
    budget: normalizeBudget(row.budget),
    max_players: normalizeMaxPlayers(row.max_players),
    role_targets: normalizeRoleTargets(row.role_targets),
    num_participants,
    exit_mode: normalizeExitMode(row.exit_mode),
    opponents: normalizeOpponents(row.opponents),
  };
}

export async function listAuctions(accessKey, pbOverride) {
  const pb = pbOverride || getPocketBase();
  // No server sort: auctions may lack created/updated autodate fields on PB.
  const rows = await pb.collection('auctions').getFullList({
    filter: `access_key="${esc(accessKey)}"`,
  });
  return rows
    .map(mapAuctionRow)
    .sort((a, b) => String(a.name).localeCompare(String(b.name), 'it'));
}

export async function fetchAuction(accessKey, auctionId, pbOverride) {
  const pb = pbOverride || getPocketBase();
  const rows = await pb.collection('auctions').getFullList({
    filter: `id="${esc(auctionId)}" && access_key="${esc(accessKey)}"`,
  });
  if (!rows.length) return null;
  return mapAuctionRow(rows[0]);
}

export async function createAuction({
  accessKey,
  name,
  budget = DEFAULT_ROSA_BUDGET,
  maxPlayers = DEFAULT_ROSA_MAX_PLAYERS,
  roleTargets = DEFAULT_ROSA_ROLE_TARGETS,
  numParticipants = null,
  exitMode = DEFAULT_EXIT_MODE,
  opponents = [],
}, pbOverride) {
  const pb = pbOverride || getPocketBase();
  const data = {
    access_key: accessKey,
    name: (name || DEFAULT_AUCTION_NAME).trim() || DEFAULT_AUCTION_NAME,
    budget: normalizeBudget(budget),
    max_players: normalizeMaxPlayers(maxPlayers),
    role_targets: buildRoleTargetsPayload(roleTargets, numParticipants),
    exit_mode: normalizeExitMode(exitMode),
    opponents: normalizeOpponents(opponents),
  };
  const row = await pb.collection('auctions').create(data);
  return mapAuctionRow(row);
}

export async function updateAuction({
  accessKey,
  auctionId,
  name,
  budget,
  maxPlayers,
  roleTargets,
  numParticipants,
  exitMode,
  opponents,
}, pbOverride) {
  const pb = pbOverride || getPocketBase();
  const existing = await pb.collection('auctions').getFullList({
    filter: `id="${esc(auctionId)}" && access_key="${esc(accessKey)}"`,
  });
  if (!existing.length) throw new Error('Asta non trovata');
  const prev = existing[0];
  const data = {
    name: (name || prev.name || DEFAULT_AUCTION_NAME).trim() || DEFAULT_AUCTION_NAME,
    budget: normalizeBudget(budget),
    max_players: normalizeMaxPlayers(maxPlayers),
    role_targets: buildRoleTargetsPayload(roleTargets, numParticipants),
    exit_mode: exitMode !== undefined
      ? normalizeExitMode(exitMode)
      : normalizeExitMode(prev.exit_mode),
    opponents: opponents !== undefined
      ? normalizeOpponents(opponents)
      : normalizeOpponents(prev.opponents),
  };
  const row = await pb.collection('auctions').update(prev.id, data);
  return mapAuctionRow(row);
}

export async function deleteAuction(accessKey, auctionId, pbOverride) {
  const pb = pbOverride || getPocketBase();
  const existing = await pb.collection('auctions').getFullList({
    filter: `id="${esc(auctionId)}" && access_key="${esc(accessKey)}"`,
  });
  if (!existing.length) return { deleted: 0 };

  const [roster, exits] = await Promise.all([
    pb.collection('roster').getFullList({
      filter: `access_key="${esc(accessKey)}" && auction_id="${esc(auctionId)}"`,
    }),
    pb.collection('auction_exits').getFullList({
      filter: `access_key="${esc(accessKey)}" && auction_id="${esc(auctionId)}"`,
    }),
  ]);
  await Promise.all([
    ...roster.map((r) => pb.collection('roster').delete(r.id)),
    ...exits.map((r) => pb.collection('auction_exits').delete(r.id)),
  ]);

  await pb.collection('auctions').delete(existing[0].id);
  return { deleted: 1 };
}
